import React, {createContext, useContext, useImperativeHandle} from 'react';
import {useDropzone} from 'react-dropzone';
import {useSnackbar} from '@netx/core-components/Snackbar';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {UploadDropzoneError} from '../locales';

export type {DropEvent} from 'react-dropzone';

export type UploaderDropzoneContextType = ReturnType<typeof useDropzone>;

export const UploaderDropzoneContext =
	createContext<UploaderDropzoneContextType>({
		acceptedFiles: [],
		draggedFiles: [],
		fileRejections: [],
		inputRef: {current: null},
		isDragActive: false,
		isDragAccept: false,
		isDragReject: false,
		isFocused: false,
		isFileDialogActive: false,
		rootRef: {current: null},
		getInputProps: () => ({} as any),
		getRootProps: () => ({} as any),
		open: () => {},
	});

export const useUploaderDropzoneProvider = () =>
	useContext(UploaderDropzoneContext);

export type UploaderDropzoneProviderRef =
	| React.RefObject<UploaderDropzoneContextType>
	| ((node: UploaderDropzoneContextType) => void)
	| null;

export type UploaderDropzoneProviderChildrenProps = UploaderDropzoneContextType;

export type UploaderDropzoneProviderProps = Parameters<
	typeof useDropzone
>[0] & {
	ref?: UploaderDropzoneProviderRef;
	children:
		| React.ReactNode
		| ((props: UploaderDropzoneProviderChildrenProps) => React.ReactNode);
};

/**
 * This is just a simple context wrapper for `react-dropzone`
 * Really not sure why they do not offer this, just makes it so we can
 * wrap a context as high as we want and then anything within in it can
 * call on `useDropzone` via `useUploaderDropzoneProvider` and we can
 * handle the onDrop stuff at a higher level
 * @param param0
 * @param ref
 * @returns
 */
const UploaderDropzoneProviderInner = (
	{children, ...props}: UploaderDropzoneProviderProps,
	ref: UploaderDropzoneProviderRef,
) => {
	const snackbar = useSnackbar();

	const dropzone = useDropzone({
		useFsAccessApi: false,
		multiple: !props.maxFiles || props.maxFiles > 1,
		...props,
		onDrop: splitEventHandler(props.onDrop, (acceptedFiles, fileRejections) => {
			if (fileRejections.length) {
				snackbar({
					message: UploadDropzoneError(fileRejections[0].errors[0].code),
					severity: 'error',
				});
			}
		}),
	});

	useImperativeHandle(ref, () => dropzone, [dropzone]);

	return (
		<UploaderDropzoneContext.Provider value={dropzone}>
			{typeof children === 'function' ? children(dropzone) : children}
		</UploaderDropzoneContext.Provider>
	);
};

export const UploaderDropzoneProviderPrimitive = React.forwardRef<
	UploaderDropzoneContextType,
	UploaderDropzoneProviderProps
>(UploaderDropzoneProviderInner);

/**
 * Asset uploader dropzone context provider
 * @example
 * <UploaderDropzoneProvider ref={dropzoneRef}>
 * 	...
 * </UploaderDropzoneProvider>
 * @example
 * <UploaderDropzoneProvider ref={dropzoneRef}>
 * 	{(dropzone) => (
 * 		...
 * 	)}
 * </UploaderDropzoneProvider>
 */
export const UploaderDropzoneProvider = UploaderDropzoneProviderPrimitive;

export default UploaderDropzoneProvider;
